import React, { useCallback, useMemo, useState } from 'react';
import styles from './chooser.module.css';

/**
 * Accordion-like Chooser component.
 *
 * Props:
 * - title: Main trigger title text.
 * - subtitle: Optional secondary text shown on the trigger.
 * - open: Controlled open state (optional).
 * - defaultOpen: Uncontrolled initial open state (optional; default false).
 * - onToggle: Callback when open state toggles.
 * - className: Optional extra class names for container.
 * - children: Content rendered inside the chooser when open.
 *
 * @param {{
 *  title: string,
 *  subtitle?: string,
 *  open?: boolean,
 *  defaultOpen?: boolean,
 *  onToggle?: (open: boolean) => void,
 *  className?: string,
 *  children?: React.ReactNode
 * }} props - Component props.
 * @returns {import('react').JSX.Element}
 */
export function Chooser({ title, subtitle = '', open, defaultOpen = false, onToggle, className = '', children }) {
  const isControlled = typeof open === 'boolean';
  const [internalOpen, setInternalOpen] = useState(!!defaultOpen);
  const isOpen = isControlled ? /** @type {boolean} */ (open) : internalOpen;

  const handleToggle = useCallback(() => {
    const next = !isOpen;
    if (!isControlled) setInternalOpen(next);
    onToggle && onToggle(next);
  }, [isOpen, isControlled, onToggle]);

  const containerClass = useMemo(
    () => `${styles['chooser']} chooser ${isOpen ? `${styles['chooser-open']} chooser-open` : ''} ${className}`.trim(),
    [isOpen, className]
  );

  return (
    <div className={containerClass}>
      {isOpen && <div className={styles['chooser--content']}>{children}</div>}
      <div className={styles['chooser--trigger']} onClick={handleToggle}>
        <div className={styles['chooser--icon']}>
          {isOpen ? (
            <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#333">
              <path d="m356-160-56-56 180-180 180 180-56 56-124-124-124 124Zm124-404L300-744l56-56 124 124 124-124 56 56-180 180Z" />
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#333">
              <path d="M480-120 300-300l58-58 122 122 122-122 58 58-180 180ZM358-598l-58-58 180-180 180 180-58 58-122-122-122 122Z" />
            </svg>
          )}
        </div>
        <div className={styles['chooser--trigger-content']}>
          <p className={styles['chooser--trigger-content-title']}>{title}</p>
          {subtitle ? <p className={styles['chooser--trigger-content-selected-title']}>{subtitle}</p> : null}
        </div>
      </div>
    </div>
  );
}

export default Chooser;
